import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SvgIcon,
} from '@mui/material';
import { useAtom } from 'jotai/index';

import ChatbotIcon from 'src/assets/chatbot/chatbot.svg?react';
import type { ChatbotTypes } from 'src/types/chatbot';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import { guidedTourState } from 'src/components/app/context/GuidedTourContext/atom';
import { GuidedTourState } from 'src/components/app/context/GuidedTourContext/constants';
import I18nLink from 'src/components/common/I18nLink';
import ChatbotChat from 'src/components/chatbot/ChatbotChat';
import {
  OpenAiContext,
  OpenAiContextProvider,
} from 'src/components/chatbot/ChatbotChat/context/openAiContext';
import {
  VirtualAssistantContext,
  VirtualAssistantContextProvider,
} from 'src/components/chatbot/ChatbotChat/context/virtualAssistantContext';

import { styles } from './index.styles';

export const CHATBOT_MODAL_BUTTON_TEST_ID = 'chatbot-modal-button';
export const CHATBOT_MODAL_ICON_TEST_ID = 'chatbot-modal-icon-test-id';
export const CHATBOT_DIALOG_TEST_ID = 'chatbot-dialog-test-id';
export const CHATBOT_DIALOG_CLOSE_BUTTON_TEST_ID =
  'chatbot-dialog-close-button';

export interface ButtonChatbotModalProps {
  chatRoomId: number | string;
  type: ChatbotTypes;
}

const ButtonChatbotModal: FC<ButtonChatbotModalProps> = ({
  chatRoomId,
  type,
}) => {
  const { t } = useTranslation('chatbot');
  const [open, setOpen] = useState(false);
  const [newMessage, setNewMessage] = useState(false);
  const [tourState] = useAtom(guidedTourState);
  const chatbotViewed = sessionStorage.getItem('chatbotViewed');
  const setChatbotViewed = () =>
    sessionStorage.setItem('chatbotViewed', 'true');

  // determine whether to auto-open chatbot window —
  // i.e. only on first EOS Dashboard visit of the session
  useEffect(() => {
    if (
      !chatbotViewed ||
      (chatbotViewed === 'false' &&
        [(GuidedTourState.INACTIVE, GuidedTourState.DISABLED)].includes(
          tourState,
        ))
    ) {
      setOpen(true);
      setChatbotViewed();
    }
  }, [chatbotViewed]);

  const toggleOpen = () => {
    setNewMessage(false);
    setOpen((prevState) => !prevState);
    setChatbotViewed();
  };

  const handleNewMessage = () => {
    setNewMessage(true);
  };

  const termsHref = history.createHref({
    pathname: routePaths.TERMS_OF_USAGE_CHATBOT,
  });

  return (
    <>
      <Box>
        {!open && newMessage && (
          <Box sx={styles.notification}>
            <MoreHorizRoundedIcon />
          </Box>
        )}
        <IconButton
          onClick={toggleOpen}
          data-test-id={CHATBOT_MODAL_BUTTON_TEST_ID}
          sx={styles.button}
        >
          <SvgIcon
            component={ChatbotIcon}
            data-test-id={CHATBOT_MODAL_ICON_TEST_ID}
            inheritViewBox
          />
        </IconButton>
      </Box>
      <Dialog
        open={open}
        onClose={toggleOpen}
        sx={styles.dialog}
        transitionDuration={400}
        data-test-id={CHATBOT_DIALOG_TEST_ID}
      >
        <DialogTitle component="div" sx={styles.header}>
          <Stack spacing={1.5} direction="row">
            <Box sx={styles.headerIcon}>
              <SvgIcon component={ChatbotIcon} inheritViewBox />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={styles.overline}>{t(`${type}.overline`)}</Box>
              <Box sx={styles.subline}>
                <Trans
                  t={t}
                  i18nKey={`${type}.subline`}
                  components={{
                    lnk: (
                      <I18nLink
                        href={termsHref}
                        rel="noopener noreferrer"
                        target="_blank"
                        color="inherit"
                      />
                    ),
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ flexGrow: 0 }}>
              <IconButton
                disableRipple
                onClick={toggleOpen}
                data-test-id={CHATBOT_DIALOG_CLOSE_BUTTON_TEST_ID}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Box>
          </Stack>
        </DialogTitle>
        <Box sx={styles.paperWrapper}>
          <DialogContent>
            {type === 'virtualAssistant' ? (
              <VirtualAssistantContextProvider
                chatRoomId={chatRoomId}
                newMessageCallback={handleNewMessage}
              >
                <ChatbotChat
                  context={VirtualAssistantContext}
                  placeholder={t(`${type}.placeholder`)}
                  showFeedbackForm
                />
              </VirtualAssistantContextProvider>
            ) : type === 'openAi' ? (
              <OpenAiContextProvider
                chatRoomId={chatRoomId}
                newMessageCallback={handleNewMessage}
              >
                <ChatbotChat
                  context={OpenAiContext}
                  placeholder={t(`${type}.placeholder`)}
                />
              </OpenAiContextProvider>
            ) : null}
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};

export default ButtonChatbotModal;
